import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Activity, AlertTriangle, FileText, ShieldCheck } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { onboardingApi, partnerApi } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

export function DashboardPage() {
  const status = useQuery({ queryKey: ['onboarding-status'], queryFn: onboardingApi.status })
  const usage = useQuery({ queryKey: ['usage'], queryFn: partnerApi.usage })

  const blockedReason = status.data?.blocked_reason

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-bold text-fg-primary">Dashboard</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Estado de tu cuenta de partner.
        </p>

        {/* Banner de bloqueo si falta aceptación */}
        {blockedReason && (
          <Link
            to={ROUTES.ONBOARDING}
            className="mt-6 flex items-center gap-3 rounded-md border border-red-300 bg-red-50 p-4 text-red-900 hover:bg-red-100 dark:bg-red-950 dark:text-red-100 dark:border-red-800"
          >
            <AlertTriangle className="h-5 w-5 flex-none" />
            <div className="flex-1">
              <p className="font-semibold">{blockedReason}</p>
              <p className="text-xs">Click para completar la aceptación de contratos →</p>
            </div>
          </Link>
        )}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card icon={ShieldCheck} label="Términos" value={status.data?.terms_up_to_date ? '✓ Vigente' : 'Pendiente'} ok={status.data?.terms_up_to_date} />
          <Card icon={ShieldCheck} label="Mandato DIAN" value={status.data?.mandate_up_to_date ? '✓ Vigente' : 'Pendiente'} ok={status.data?.mandate_up_to_date} />
          <Card icon={FileText} label="Docs este mes" value={(usage.data?.docs_per_month.current ?? 0).toLocaleString('es-CO')} sub={`/ ${(usage.data?.docs_per_month.limit ?? 0).toLocaleString('es-CO')}`} />
          <Card icon={Activity} label="Webhooks hoy" value={(usage.data?.webhook_deliveries_per_day.current ?? 0).toLocaleString('es-CO')} sub={`/ ${(usage.data?.webhook_deliveries_per_day.limit ?? 0).toLocaleString('es-CO')}`} />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ActionCard title="Ver guía de integración" desc="curl + Go + Node + Python" to={ROUTES.INTEGRATION_GUIDE} />
          <ActionCard title="Probar en sandbox" desc="DIAN-hab — emite 1 doc test" to={ROUTES.SANDBOX} />
          <ActionCard title="Rotar API keys" desc="CRÍTICO — invalida anteriores" to={ROUTES.API_KEYS} />
          <ActionCard title="Subir cert ECD propio" desc="Si quieres BYO en lugar del mandato" to={ROUTES.CERT} />
        </div>
      </div>
    </AppShell>
  )
}

function Card({
  icon: Icon,
  label,
  value,
  sub,
  ok,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  sub?: string
  ok?: boolean
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-surface p-5">
      <div className="flex items-start justify-between">
        <Icon className={`h-5 w-5 ${ok === false ? 'text-red-600' : ok === true ? 'text-green-600' : 'text-fg-muted'}`} />
      </div>
      <div className="mt-4">
        <p className={`text-2xl font-bold tracking-tight ${ok === false ? 'text-red-700' : 'text-fg-primary'}`}>{value}</p>
        {sub && <p className="text-xs text-fg-muted">{sub}</p>}
        <p className="mt-1 text-sm text-fg-secondary">{label}</p>
      </div>
    </div>
  )
}

function ActionCard({ title, desc, to }: { title: string; desc: string; to: string }) {
  return (
    <Link to={to} className="block rounded-lg border border-border bg-bg-surface p-5 hover:border-brand-500">
      <p className="font-semibold text-fg-primary">{title}</p>
      <p className="mt-1 text-xs text-fg-secondary">{desc}</p>
    </Link>
  )
}
